import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useResolvedPath } from 'react-router-dom';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useResizeObserver } from 'usehooks-ts';

import { useAppDispatch, useAppSelector } from '@store/hooks';

import { Table, Input, notification, Form, Flex, Modal, Drawer, Button, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import type { GetRef } from 'antd';

import { LoadingOutlined, ReloadOutlined, HistoryOutlined, SwapOutlined, PlayCircleOutlined } from '@ant-design/icons';

import Transactions from '@components/transactions/transactions';
import ExchangeRateInfo from '@components/exchange-rate-info/exchange-rate-info';
import DepositingModal from '@components/balance-details/depositing-modal';
import TransferAccountsModal from '@components/balance-details/transfer-accounts-modal';
import RefundModal from '@components/balance-details/refund-modal';

import Filter from '@controls/filter/filter';
import Toolbar from '@controls/toolbar/toolbar';
import Account from '@controls/account';

import { serverFetch } from '@src/server';

import { securityRestriction } from '@src/extensions/notification';
import { exception } from '@extensions/notification';
import { delayAction, toFinanceString } from '@extensions/utils';
import { userLoaded, setFilter } from '@store/actions';

import { IUserBalanceFilter } from '@entities/user-balance-filter';
import { IUserBalance } from '@entities/user-balance';
import { IUserSession } from '@src/entities/user-session';
import { Permission, hasPermission } from '@src/enums/permission';
import { Currency } from '@enums/currency';

import { BillIcon, WalletIcon, RefundIcon } from '@icons/index';

dayjs.extend(utc);

type TableRef = GetRef<typeof Table>;

const filterContext: string = 'Balances';

const Balances = () => {
    const initFilter: IUserBalanceFilter = {};

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const navigate = useNavigate();
    const resolved = useResolvedPath('../finance');

    const [modal, modalContextHolder] = Modal.useModal();

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const tableRef = useRef<TableRef>(null);

    const filter = useAppSelector<IUserBalanceFilter>((s) => s.filters[filterContext]);

    const d = useAppDispatch();

    const [api, notificationContextHolder] = notification.useNotification();

    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [balances, setBalances] = useState<Array<IUserBalance>>([]);
    const [currentBalance, setCurrentBalance] = useState<IUserBalance>();
    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [totalBalance, setTotalBalance] = useState<number>(0);

    const [lastSelectedKey, setLastSelectedKey] = useState<React.Key>();

    const [openDepositingModal, setOpenDepositingModal] = useState<boolean>(false);
    const [openTransferAccountsModal, setOpenTransferAccountsModal] = useState<boolean>(false);
    const [openRefundModal, setOpenRefundModal] = useState<boolean>(false);
    const [openTransactions, setOpenTransactions] = useState<boolean>(false);

    useEffect(() => {
        let cleanup = false;

        if (!refreshRequired) return;

        setSelectedIds([]);
        setCurrentBalance(undefined);
        setBalances([]);

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch('userbalances', { method: 'GET', queryParams: filter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения балансов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                const entities: Array<IUserBalance> = result[0][0];

                setBalances(entities);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!loading && lastSelectedKey) {
            tableRef.current?.scrollTo({ key: lastSelectedKey });
            setLastSelectedKey(undefined);
        }
    }, [loading]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));
            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    useEffect(() => {
        if (!balances) return;

        let balance: number = 0;

        balances.map((b) => {
            balance += b.amount || 0;
        });

        setTotalBalance(balance);
    }, [balances]);

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            setLastSelectedKey(selectedRowKeys[0]);

            const entity = balances.find((o) => o.userId == selectedRowKeys[0]);
            setCurrentBalance(entity);
        } else {
            setCurrentBalance(undefined);
        }
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            style={{ width: 150 }}
                            key='login'
                            placeholder='Логин'
                            value={filter?.login}
                            onChange={(data: any) => {
                                d(setFilter({ ...filter, login: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 300 }}
                            key='searchText'
                            placeholder='Контрагент'
                            value={filter?.searchText}
                            onChange={(data: any) => {
                                d(setFilter({ ...filter, searchText: data.target.value }, filterContext));
                            }}
                        />,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Счета на оплату',
                        key: 'bills',
                        disabled: loading || !currentBalance,
                        icon: <BillIcon />,
                        onClick: () => {
                            navigate(`${resolved.pathname}/bills/${currentBalance?.userId}`);
                        },
                    },
                    /*  {
                        label: 'Инициализировать балансы v2',
                        key: 'run',
                        icon: <PlayCircleOutlined />,
                        hidden: !hasPermission(userSession.permissions, Permission.FullAccess),
                        onClick: () => {
                            setLoading(true);

                            serverFetch(`userbalances/init`, { method: 'POST' })
                                .then(() => {
                                    setLoading(false);
                                    success(api, 'Инициализация успешно завершена');
                                })
                                .catch((ex) => {
                                    setLoading(false);
                                    exception(api, 'Ошибка инициализации балансов v2', ex, () => d(userLoaded(undefined)));
                                });
                        },
                    }, */
                ]}
                farControl={<ExchangeRateInfo setInternalUsdExchangeRate={true} api={api} serverFetch={serverFetch} />}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<IUserBalance> = [
            {
                title: 'Логин',
                width: 150,
                dataIndex: 'userLogin',
                render: (_, record) => {
                    return (
                        <div>
                            <span style={{ marginRight: 5 }}>
                                <a
                                    onClick={() => {
                                        navigate(`${resolved.pathname}/balances/${record?.userId}`);
                                    }}
                                >
                                    {record.userLogin}
                                </a>
                            </span>

                            <Flex align='center' gap='small' style={{ marginTop: 7 }}>
                                <Tooltip placement='topLeft' title='Внести денежные средства на счет'>
                                    <Button
                                        icon={<WalletIcon style={{ fontSize: 16 }} />}
                                        size='small'
                                        onClick={() => {
                                            if (!hasPermission(userSession.permissions, Permission.ManageBalance)) {
                                                securityRestriction(api, [Permission.ManageBalance]);
                                                return;
                                            }

                                            setCurrentBalance(balances.find((b) => b.userId == record.userId));
                                            setOpenDepositingModal(true);
                                        }}
                                    />
                                </Tooltip>

                                <Tooltip placement='topLeft' title='Перевод между счетами'>
                                    <Button
                                        icon={<SwapOutlined />}
                                        size='small'
                                        onClick={() => {
                                            if (!hasPermission(userSession.permissions, Permission.ManageBalance)) {
                                                securityRestriction(api, [Permission.ManageBalance]);
                                                return;
                                            }

                                            setCurrentBalance(balances.find((b) => b.userId == record.userId));
                                            setOpenTransferAccountsModal(true);
                                        }}
                                    />
                                </Tooltip>

                                <Tooltip placement='topLeft' title='Возврат денежных средств'>
                                    <Button
                                        icon={<RefundIcon style={{ fontSize: 16 }} />}
                                        size='small'
                                        onClick={() => {
                                            if (!hasPermission(userSession.permissions, Permission.ManageBalance)) {
                                                securityRestriction(api, [Permission.ManageBalance]);
                                                return;
                                            }

                                            setCurrentBalance(balances.find((b) => b.userId == record.userId));
                                            setOpenRefundModal(true);
                                        }}
                                    />
                                </Tooltip>

                                <Tooltip placement='topLeft' title='История операций'>
                                    <Button
                                        icon={<HistoryOutlined />}
                                        size='small'
                                        onClick={() => {
                                            setCurrentBalance(balances.find((b) => b.userId == record.userId));
                                            setOpenTransactions(true);
                                        }}
                                    />
                                </Tooltip>
                            </Flex>

                            <Flex align='start' gap='small' vertical={true} style={{ marginTop: 7 }}>
                                {record.useCashPayments && (
                                    <Tag color='green' style={{ color: '#000000' }}>
                                        Наличный
                                    </Tag>
                                )}
                                {record.useNonCashPayments && (
                                    <Tag color='#ffee96' style={{ color: '#000000' }}>
                                        Безналичный
                                    </Tag>
                                )}
                            </Flex>
                        </div>
                    );
                },
            },
            {
                title: 'Контрагент',
                width: 350,
                render: (_, record) => {
                    return (
                        <>
                            <div>{record.legalName}</div>
                            <div>{record.inn}</div>
                        </>
                    );
                },
            },
            {
                title: 'Расчётный счёт',
                width: 150,
                onCell: (record) => ({
                    style: {
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return (
                        <Flex gap='small' vertical={true}>
                            {record.userAccounts?.map((a) => {
                                return (
                                    <Account
                                        key={`${a.type}${a.currency}`}
                                        type={a.type}
                                        currency={a.currency}
                                        amount={a.amount}
                                        userBalanceType={a.userBalanceType}
                                    />
                                );
                            })}
                        </Flex>
                    );
                },
            },
            {
                title: 'Баланс $',
                width: 200,
                align: 'center',
                onCell: (record) => ({
                    style: {
                        fontWeight: 600,
                        color: '#FFFFFF',
                        background: record.amount >= 0 ? 'var(--main-green)' : 'var(--main-red)',
                    },
                }),
                render: (_, record) => {
                    return <span>{toFinanceString(record.amount || 0, 2)}</span>;
                },
            },
            {},
        ];

        return (
            <Table
                ref={tableRef}
                rowKey='userId'
                size='small'
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                columns={columns}
                dataSource={balances}
                pagination={false}
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    columnWidth: 32,
                    type: 'radio',
                }}
                onRow={(record: IUserBalance) => {
                    return {
                        onClick: (event) => {
                            if (!record.userId) return;

                            onSelectChange([record.userId]);
                        },
                    };
                }}
                virtual={true}
                footer={() => {
                    return (
                        <Flex justify='start' style={{ fontWeight: 700, fontSize: 16 }} align='center'>
                            <span>Итого:</span>
                            <span style={{ marginLeft: 5, color: totalBalance >= 0 ? 'var(--main-green)' : 'var(--main-red)' }}>
                                {toFinanceString(totalBalance, 2)} $
                            </span>
                        </Flex>
                    );
                }}
                scroll={{ y: height }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 233px - ${filterHeight}px + ${showFilter ? 0 : 16}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {currentBalance && openDepositingModal && (
                <DepositingModal
                    userId={currentBalance.userId}
                    userLogin={currentBalance.userLogin}
                    onCancel={() => setOpenDepositingModal(false)}
                    onSave={() => {
                        setOpenDepositingModal(false);
                        setRefreshRequired(true);
                    }}
                    api={api}
                    serverFetch={serverFetch}
                />
            )}

            {currentBalance && openTransferAccountsModal && (
                <TransferAccountsModal
                    userId={currentBalance.userId}
                    userLogin={currentBalance.userLogin}
                    accounts={currentBalance.userAccounts || []}
                    onCancel={() => setOpenTransferAccountsModal(false)}
                    onSave={() => {
                        setRefreshRequired(true);
                        setOpenTransferAccountsModal(false);
                    }}
                    api={api}
                    serverFetch={serverFetch}
                />
            )}

            {currentBalance && openRefundModal && (
                <RefundModal
                    userId={currentBalance.userId}
                    userLogin={currentBalance.userLogin}
                    accounts={currentBalance.userAccounts || []}
                    onCancel={() => setOpenRefundModal(false)}
                    onSave={() => {
                        setOpenRefundModal(false);
                        setRefreshRequired(true);
                    }}
                    api={api}
                    serverFetch={serverFetch}
                />
            )}

            {currentBalance && openTransactions && (
                <Drawer
                    closable
                    destroyOnHidden
                    title={`Операции "${currentBalance.userLogin}"`}
                    placement='right'
                    open={true}
                    onClose={() => setOpenTransactions(false)}
                    size={1200}
                >
                    <Transactions
                        userId={currentBalance.userId}
                        isViewMode={!hasPermission(userSession.permissions, Permission.ManageBalance)}
                        useTagRequired={!!currentBalance.tag}
                        tag={currentBalance.tag}
                        currency={Currency.Usd}
                        onRefresh={() => setRefreshRequired(true)}
                        serverFetch={serverFetch}
                    />
                </Drawer>
            )}

            {notificationContextHolder}
            {modalContextHolder}
        </div>
    );
};

export default Balances;
